import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'
import en from './en.json'
import id from './id.json'
import { tg } from '../lib/telegram'

export const SUPPORTED_LANGUAGES = ['en', 'id'] as const
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

const STORAGE_KEY = 'swipify_lang'

function isSupported(code: string | undefined): code is SupportedLanguage {
  return !!code && (SUPPORTED_LANGUAGES as readonly string[]).includes(code)
}

/**
 * Resolves the language to boot with, before the backend has had a chance
 * to respond. Preference order: a previously-saved choice (from this device
 * or a prior /me sync) → Telegram's client language → English.
 *
 * Once /me resolves, App.tsx calls setLanguage(me.locale) — the backend's
 * persisted value (which honors a manual SettingsSheet pick) always wins
 * over whatever we guessed here.
 */
function resolveInitialLanguage(): SupportedLanguage {
  const stored = localStorage.getItem(STORAGE_KEY) ?? undefined
  if (isSupported(stored)) return stored

  const tgCode = tg()?.initDataUnsafe.user?.language_code
  const base = tgCode?.split('-')[0]
  if (isSupported(base)) return base

  return 'en'
}

void i18next.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    id: { translation: id },
  },
  lng: resolveInitialLanguage(),
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
})

/** Switches the active language and persists the choice locally. */
export function setLanguage(code: string): void {
  if (!isSupported(code)) return
  localStorage.setItem(STORAGE_KEY, code)
  if (i18next.language !== code) {
    void i18next.changeLanguage(code)
  }
}

export default i18next
